import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { SizedText } from "../../styles/styles";
import { Articles } from "../../types/article";
import { useUserId } from "../../../../core/auth/useLoggedInUser";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaPrompt } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaPrompt";
import TrashCanIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/TrashCanIconPath"; // NOSONAR
import { setOngoingTasksToClosed } from "../../tools";
import { Trans, useTranslation } from "@coworker/locales";
import { ModalTypes } from "../../types/views";
import { Group } from "../../types/groups";
import { useDeleteGroupById } from "../../hooks/useDeleteGroupById";
import { useTasksByMedia } from "../../hooks/useTasksByMedia";
import { OngoingTaskLinksResponse } from "../../types/taskLink";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";

interface DeleteGroupModalProps {
  modalVisibility: boolean;
  mediaId: string;
  allArticles: Articles;
  currentGroup?: Group | undefined;
  onTriggerNewModal: (modalType: ModalTypes) => void;
}

export const DeleteGroupModal = ({
  modalVisibility,
  mediaId,
  allArticles,
  currentGroup,
  onTriggerNewModal,
}: DeleteGroupModalProps) => {
  const { showToast } = useToastNotification();
  const { t } = useTranslation();
  const userId = useUserId();
  const tasks = useTasksByMedia(mediaId);
  const deleteGroupById = useDeleteGroupById();

  const handleDeleteMedia = async () => {
    if (currentGroup === undefined) return;
    const articlesIdsInGroup = allArticles
      .filter((article) => article.groupId === currentGroup.id)
      .map((article) => article.id);
    const tasksInGroup = tasks?.tasks?.filter((task) =>
      articlesIdsInGroup.includes(task.article_id)
    );

    await setOngoingTasksToClosed(
      mediaId,
      t("taskClosedBecauseRemovedArticleString"),
      {
        count: tasksInGroup?.length ?? 0,
        tasks: tasksInGroup ?? [],
      } as OngoingTaskLinksResponse,
      userId
    );
    try {
      await deleteGroupById.mutateAsync({
        groupId: currentGroup.id,
        mediaId: mediaId,
      });
      onTriggerNewModal(ModalTypes.DEFAULT_VIEW);
      showToast(t("groupDeletedString"));
    } catch {
      alert(t("searchErrorString"));
    }
  };

  return (
    <>
      <FixaModal
        visible={modalVisibility}
        handleCloseBtn={() => onTriggerNewModal(ModalTypes.DEFAULT_VIEW)}
      >
        <FixaPrompt
          title={""}
          titleId={`delete-${currentGroup?.id || "1"}`}
          header={
            <FixaModalHeader
              title={t("deleteGroupString")}
              ariaCloseTxt="Close prompt."
            />
          }
          footer={
            <FixaModalFooter>
              <FixaButton
                text={t("cancelString")}
                type="secondary"
                onClick={() => onTriggerNewModal(ModalTypes.DEFAULT_VIEW)}
              />
              <FixaButton
                type="danger"
                text={t("deleteString")}
                ssrIcon={TrashCanIconPath}
                onClick={handleDeleteMedia}
                style={{
                  backgroundColor: "var(--pink)",
                  color: "var(--white)",
                }}
              />
            </FixaModalFooter>
          }
        >
          <>
            <SizedText $fontSize={16} $bold={true}>
              <Trans values={{ item: currentGroup?.name || "" }}>
                sureToDeleteGroupString
              </Trans>
            </SizedText>
            <SizedText $fontSize={14} $bold={false}>
              <Trans>deleteGroupAndAllArticlesString</Trans> <br />
              <Trans>canNotBeUndoneString</Trans>
            </SizedText>
          </>
        </FixaPrompt>
      </FixaModal>
    </>
  );
};
